import _ from 'underscore';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';

import { Deserializer } from './deserializer';
import { PermanentFilterStorage } from './permanent-filter-storage';
import { taskQueueMixin } from './task-queue-mixin';
import type { FilterObject, FilterStorageOptions } from './types';

export class EditableFilterPermanentStorage {
  deserializer: Deserializer;
  permanentFilterStorage: PermanentFilterStorage;

  constructor(options: FilterStorageOptions = {}) {
    const {
      featureName,
      entityName,
      preferenceService,
      storageKey,
    } = options;

    this.permanentFilterStorage = new PermanentFilterStorage({
      featureName,
      preferenceService,
      storageKey: this.getStorageKey({
        featureName,
        entityName,
        storageKey,
      }),
      isAccountLevel: true,
    });
    this.deserializer = new Deserializer(options);

    taskQueueMixin(this, ['get', 'save', 'clean']);
  }

  /**
   * @private
   * @return {String} - resource and entry ids
   */
  getStorageKey({
    featureName,
    entityName,
    storageKey,
  }: { featureName?: string; entityName?: string; storageKey?: string }) {
    return storageKey || `editable-filter-storage-campaignwebui-${featureName}-${entityName}/EditableFilter`;
  }

  /**
=   * @private
   * @param {string} key - The key of the current property being parsed
   * @param {any} value - The value of the current property being parsed
   * @returns {any} - Transformed value (BigInt if applicable)
   */
  bigIntReviver(key: string, value: any) {
    if (typeof value === 'string' && /^\d+$/.test(value)) {
      if (!Number.isSafeInteger(parseInt(value, 10))) {
        return BigInt(value);
      }
    }

    return value;
  }

  /**
   * @returns {Object} Promise that resolve to Filter object that contains query,
   * name and id
  */
  get() {
    return this.permanentFilterStorage.get()
      .then((result = {}) => {
        try {
          if (_.isObject(result) && result.filter) {
            const savedFilter = result.filter;

            const filter = _.isString(savedFilter) ?
              this.deserializer.deserialize(JSON.parse(savedFilter, this.bigIntReviver)) :
              this.deserializer.deserialize(cloneDeep(savedFilter));

            return !_.isEmpty(filter.query) ? { filter } : {};
          }
          return JSON.parse(result as any, this.bigIntReviver);
        } catch (err) {
          throw new Error('Invalid filter JSON string');
        }
      })
      .catch(() => ({}));
  }

  /**
   * Save quick filter key and query to session
   * @param {Object} filter - Filter object that contains query, name and id
   * @return {undefined}
   */
  save(filter: FilterObject) {
    const serializedValue = JSON.stringify(_.pick(filter, 'query', 'name', 'id'), (key, value) => {
      if (typeof value === 'bigint') {
        return value.toString();
      }
      return value;
    });
    return this.permanentFilterStorage.save(serializedValue);
  }

  clean() {
    return this.permanentFilterStorage.delete();
  }
}
